<template>
  <!-- 
    When the component displays the form `content-overflow-y` has to be set to
    'visible' so that the select works correctly. When it's showing errors the
    value 'auto' has to be set to make it scrollable.
  -->
  <modal-wrapper
    ref="modalRef"
    :busy="isImporting"
    :close-on-ok-click="false"
    :content-overflow-y="isImportErrors ? 'auto' : 'visible'"
    :display-cancel-button="!importResult"
    :header="t('iblUploadModal.title')"
    :ok-button-label="okButtonLabel"
    :size="ModalSize.XL"
    @ok-button-click="handleModalOk"
  >
    <!--
      Showing import result component with detailed errors and warnings if
      there were any. If the import was fully successful, a success toast is
      shown and the modal is closed.
    -->
    <ibl-import-result-display
      v-if="isImportErrors"
      :import-result="importResult!"
    />
    <div v-else class="form">
      <div v-if="isSupplierSelect">
        <label>{{ $t('iblUploadModal.supplier') }}</label>
        <project-suppliers-select
          v-model="selectedSupplier"
          :is-error="isSupplierSelectError"
          :project-id="projectId"
        />
        <message-wrapper v-if="isSupplierSelectError" simple-error>
          {{ $t('iblUploadModal.noSupplierSelected') }}
        </message-wrapper>
      </div>
      <div>
        <label>{{ t('iblUploadModal.iblFile') }}</label>
        <file-upload-wrapper
          v-model:file="file"
          accept=".xlsx"
          :invalid="isNoFileError"
        />
        <message-wrapper v-if="isNoFileError" simple-error>
          {{ $t('generic.noFileSelected') }}
        </message-wrapper>
        <message-wrapper
          class="max-kks-count-hint"
          input-description
          severity="secondary"
          size="small"
          variant="simple"
          >{{ getMaxKksCountHint }}</message-wrapper
        >
      </div>
    </div>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { useStore } from 'vuex';
  import { useRoles } from '@/composables/useRoles';
  import { ModalSize } from '@/components/ModalWrapper/ModalWrapper.types';
  import ProjectSuppliersSelect, {
    SupplierOption,
  } from '../ProjectSuppliersSelect/ProjectSuppliersSelect.vue';
  import IblImportResultDisplay from '../IblImportResultDisplay/IblImportResultDisplay.vue';
  import {
    IIblImportResponseDto,
    ProjectType,
    IblErrorCode,
    IBL_IMPORT_MAX_KKS_COUNT,
    IblImportResult,
    Role,
  } from 'types';
  import { IblImportService } from '@/services';
  import { isCustomErrorCodeException } from '@/helpers';
  import { useModalWrapper } from '../ModalWrapper';

  const props = defineProps<{
    projectId: string;
    projectType: ProjectType;
  }>();

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const store = useStore();
  const { isUserRole } = useRoles();
  const emit = defineEmits<{
    (e: 'ibl-import-finished'): void;
  }>();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const file = ref<File | null>(null);
  const importResult = ref<IIblImportResponseDto | null>(null);
  const isImporting = ref(false);
  const isImportErrors = ref(false);
  const isSubmitted = ref(false);
  const selectedSupplier = ref<SupplierOption | null>(null);

  const isSupplierProject = computed(
    () => props.projectType === ProjectType.SUPPLIER_PROJECT,
  );
  const isSupplierSelect = computed(
    () =>
      isSupplierProject.value &&
      /**
       * Only admins can select the supplier for which the IBL is uploaded.
       * In other cases the supplier is established automatically.
       */
      isUserRole([Role.Admin]),
  );
  const okButtonLabel = computed(() =>
    importResult.value
      ? t('generic.ok').toString()
      : t('generic.upload').toString(),
  );
  /** For project-suppliers-select component */
  const isSupplierSelectError = computed(
    () => !!(isSubmitted.value && !selectedSupplier.value),
  );
  const isNoFileError = computed(() => isSubmitted.value && !file.value);
  const getMaxKksCountHint = computed(() =>
    t('iblUploadModal.maxKksCount', {
      maxKksCount: IBL_IMPORT_MAX_KKS_COUNT,
    }).toString(),
  );

  function handleModalOk(): void {
    if (importResult.value) {
      hide();
    } else handleSubmit();
  }

  async function handleSubmit(): Promise<void> {
    isSubmitted.value = true;
    if (!validateForm()) return;

    isImporting.value = true;
    try {
      importResult.value = await IblImportService.importIbl(
        file.value!,
        props.projectId,
        selectedSupplier.value?._id,
      );
      /**
       * If the import was successful, close the modal and show success
       * toast.
       */
      if (importResult.value.importResult === IblImportResult.SUCCESS) {
        createSuccessToast(t('iblUploadModal.successfullyUploadedIbl'));
        hide();
      } else {
        /**
         * If there were errors or warnings, show the import result
         * component with detailed errors.
         */
        isImportErrors.value = true;
      }

      emit('ibl-import-finished');
    } catch (e) {
      if (isCustomErrorCodeException(e)) {
        if (
          e.response?.data.customErrorCode === IblErrorCode.WRONG_IBL_FORMAT
        ) {
          createErrorToast(t('iblUploadModal.errors.wrongIblFormat'));
        } else if (
          e.response?.data.customErrorCode ===
          IblErrorCode.MAX_KKS_COUNT_EXCEEDED
        ) {
          createErrorToast(
            t('iblUploadModal.errors.maxKksCountExceeded', {
              maxKksCount: IBL_IMPORT_MAX_KKS_COUNT,
            }),
          );
        } else {
          createErrorToast(t('iblUploadModal.errors.errorUploadingIbl'));
        }
      } else {
        createErrorToast(t('iblUploadModal.errors.errorUploadingIbl'));
      }
    } finally {
      isImporting.value = false;
    }
  }

  function validateForm(): boolean {
    return isSupplierProject.value
      ? !!selectedSupplier.value && !!file.value
      : !!file.value;
  }

  /**
   * If the project is a supplier project, the supplier is selected
   * automatically. If the user is an admin, the supplier can be selected
   * manually.
   */
  onMounted(() => {
    if (isSupplierProject.value && store.state.supplierId) {
      selectedSupplier.value = {
        _id: store.state.supplierId,
      } as SupplierOption;
    }
  });
</script>

<style lang="scss" scoped>
  .form {
    display: flex;
    flex-direction: column;
    row-gap: 1rem;

    label {
      margin-bottom: 0.5rem;
    }

    .max-kks-count-hint {
      margin-top: 0.3rem;
    }
  }
</style>
